import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { HeartIcon, BookOpenIcon, FlameIcon, HeartHandshakeIcon, UsersIcon, ListIcon, AlertCircleIcon, MapPinIcon, UserIcon, GlobeIcon, LinkIcon, ZapIcon, BookmarkIcon } from 'lucide-react';
import { FilterSidebar, FilterSidebarWrapper, NavItem } from '../components/UI/FilterSidebar';
import ContentPageHeader, { getContentPageIcon } from '../components/Content/ContentPageHeader';
import { ContentAccordionSection } from '../components/UI/ContentAccordion';
import { useDarkClasses } from '../hooks/useDarkClasses';
import { useDarkMode } from '../context/DarkModeContext';
import BackButton from '../components/UI/BackButton';
// Identity type
type IdentityType = 'indigenous' | 'diaspora' | null;
interface CulturalIdentity {
  id: string;
  name: string;
  description: string;
  count: number;
  type: IdentityType;
  region: string;
}
// 10 regions — variable count per region; Indigenous entries are distinct from national entries
const culturalIdentities: CulturalIdentity[] = [
// ── Oceania ──
{
  id: 'maori',
  name: 'Māori',
  description: 'Indigenous Polynesian people of Aotearoa New Zealand, with distinct language, tikanga, and whakapapa.',
  count: 64,
  type: 'indigenous',
  region: 'Oceania'
}, {
  id: 'new-zealander',
  name: 'New Zealander',
  description: 'Contemporary New Zealand national identity, shaped by Māori, Pākehā, and Pacific cultures.',
  count: 58,
  type: null,
  region: 'Oceania'
}, {
  id: 'aboriginal-australian',
  name: 'Aboriginal Australian',
  description: "The world's oldest living cultures, with deep connections to Country, Dreaming, and oral tradition.",
  count: 87,
  type: 'indigenous',
  region: 'Oceania'
}, {
  id: 'torres-strait-islander',
  name: 'Torres Strait Islander',
  description: 'Indigenous peoples of the Torres Strait Islands, distinct from Aboriginal Australians in culture and heritage.',
  count: 33,
  type: 'indigenous',
  region: 'Oceania'
}, {
  id: 'australian',
  name: 'Australian',
  description: "Contemporary Australian national identity, one of the world's most multicultural societies.",
  count: 72,
  type: null,
  region: 'Oceania'
}, {
  id: 'pacific-islander',
  name: 'Pacific Islander',
  description: 'Cultures spanning Polynesia, Melanesia, and Micronesia — Samoan, Tongan, Fijian, and beyond.',
  count: 52,
  type: null,
  region: 'Oceania'
}, {
  id: 'filipino',
  name: 'Filipino',
  description: 'Rich traditions from the Philippine archipelago, including Tagalog, Visayan, and Ilocano cultures.',
  count: 78,
  type: null,
  region: 'Oceania'
}, {
  id: 'vietnamese-australian',
  name: 'Vietnamese Australian',
  description: 'Diasporic Vietnamese experiences in Australia, navigating dual cultural identity.',
  count: 41,
  type: 'diaspora',
  region: 'Oceania'
},
// ── Africa ──
{
  id: 'igbo',
  name: 'Igbo',
  description: "One of Nigeria's largest ethnic groups, with rich oral traditions, art, and the Odinani belief system.",
  count: 56,
  type: 'indigenous',
  region: 'Africa'
}, {
  id: 'zulu',
  name: 'Zulu',
  description: 'The largest ethnic group in South Africa, with a proud heritage of oral poetry, music, and warrior tradition.',
  count: 48,
  type: 'indigenous',
  region: 'Africa'
}, {
  id: 'nigerian',
  name: 'Nigerian',
  description: "Stories from Africa's most populous nation, spanning Yoruba, Hausa, Igbo, and hundreds of other cultures.",
  count: 112,
  type: null,
  region: 'Africa'
}, {
  id: 'kenyan',
  name: 'Kenyan',
  description: 'Contemporary Kenyan identity, home to over 40 ethnic groups including Kikuyu, Luo, and Maasai.',
  count: 76,
  type: null,
  region: 'Africa'
}, {
  id: 'ghanaian',
  name: 'Ghanaian',
  description: "West African stories rooted in Akan, Ewe, and Ga cultures, and Ghana's legacy as a pan-African beacon.",
  count: 68,
  type: null,
  region: 'Africa'
}, {
  id: 'ethiopian',
  name: 'Ethiopian',
  description: "One of the world's oldest civilisations, with Amhara, Oromo, Tigrinya, and Somali cultural threads.",
  count: 59,
  type: null,
  region: 'Africa'
}, {
  id: 'north-african',
  name: 'North African',
  description: 'Cultures of Egypt, Morocco, Algeria, Tunisia, and Libya — Arab, Amazigh, and Mediterranean.',
  count: 89,
  type: null,
  region: 'Africa'
}, {
  id: 'afro-caribbean',
  name: 'Afro-Caribbean',
  description: 'African-descended cultures of the Caribbean islands, shaped by history, resistance, and vibrant creativity.',
  count: 72,
  type: null,
  region: 'Africa'
},
// ── Europe ──
{
  id: 'romani',
  name: 'Romani',
  description: "Europe's largest stateless ethnic minority, with a rich nomadic heritage, music, and oral tradition.",
  count: 43,
  type: 'indigenous',
  region: 'Europe'
}, {
  id: 'sami',
  name: 'Sámi',
  description: 'Indigenous peoples of Sápmi, spanning northern Norway, Sweden, Finland, and Russia.',
  count: 29,
  type: 'indigenous',
  region: 'Europe'
}, {
  id: 'irish',
  name: 'Irish',
  description: 'Celtic traditions, mythology, and the modern Irish experience at home and in the diaspora.',
  count: 112,
  type: null,
  region: 'Europe'
}, {
  id: 'jewish-european',
  name: 'Jewish European',
  description: "Ashkenazi, Sephardic, and Mizrahi Jewish experiences across Europe's complex history.",
  count: 96,
  type: null,
  region: 'Europe'
}, {
  id: 'black-british',
  name: 'Black British',
  description: 'African and Caribbean-descended communities in the United Kingdom, shaping British culture for generations.',
  count: 84,
  type: 'diaspora',
  region: 'Europe'
}, {
  id: 'south-asian-british',
  name: 'South Asian British',
  description: 'British-Indian, Pakistani, and Bangladeshi diasporic experiences — navigating heritage and belonging.',
  count: 91,
  type: 'diaspora',
  region: 'Europe'
}, {
  id: 'eastern-european',
  name: 'Eastern European',
  description: 'Cultures of Poland, Ukraine, Romania, Hungary, and neighbouring nations — history, resilience, and identity.',
  count: 67,
  type: null,
  region: 'Europe'
}, {
  id: 'british',
  name: 'British',
  description: "Contemporary British identity in one of the world's most diverse and historically complex nations.",
  count: 103,
  type: null,
  region: 'Europe'
},
// ── North America ──
{
  id: 'cherokee',
  name: 'Cherokee',
  description: 'One of the largest Indigenous nations of the southeastern United States, with a written language and rich storytelling tradition.',
  count: 54,
  type: 'indigenous',
  region: 'North America'
}, {
  id: 'lakota',
  name: 'Lakota',
  description: 'Sioux Nation peoples of the Great Plains, known for spiritual traditions, warrior culture, and resistance.',
  count: 47,
  type: 'indigenous',
  region: 'North America'
}, {
  id: 'haudenosaunee',
  name: 'Haudenosaunee',
  description: 'The Iroquois Confederacy — Mohawk, Oneida, Onondaga, Cayuga, Seneca, and Tuscarora nations.',
  count: 38,
  type: 'indigenous',
  region: 'North America'
}, {
  id: 'first-nations-canadian',
  name: 'First Nations Canadian',
  description: "Indigenous peoples of Canada including Cree, Ojibwe, Mi'kmaq, and hundreds of distinct nations.",
  count: 76,
  type: 'indigenous',
  region: 'North America'
}, {
  id: 'african-american',
  name: 'African American',
  description: 'The culture, history, and contemporary experience of Black Americans — from the Great Migration to today.',
  count: 198,
  type: null,
  region: 'North America'
}, {
  id: 'mexican-american',
  name: 'Mexican American',
  description: 'Chicano and Mexican-American identity — straddling the border, blending cultures, forging something new.',
  count: 143,
  type: null,
  region: 'North America'
}, {
  id: 'caribbean-american',
  name: 'Caribbean American',
  description: 'Diasporic Caribbean communities across the United States — Haitian, Jamaican, Dominican, and more.',
  count: 87,
  type: 'diaspora',
  region: 'North America'
}, {
  id: 'american',
  name: 'American',
  description: 'Contemporary American national identity in all its complexity, contradiction, and diversity.',
  count: 167,
  type: null,
  region: 'North America'
}, {
  id: 'canadian',
  name: 'Canadian',
  description: 'Canadian identity — multicultural, bilingual, and shaped by Indigenous, French, British, and immigrant histories.',
  count: 98,
  type: null,
  region: 'North America'
},
// ── East Asia ──
{
  id: 'mongolian',
  name: 'Mongolian',
  description: "Nomadic heritage and contemporary life on the steppe — one of the world's great horse cultures.",
  count: 38,
  type: 'indigenous',
  region: 'East Asia'
}, {
  id: 'tibetan',
  name: 'Tibetan',
  description: 'Tibetan Buddhist culture, the Roof of the World, and a diaspora carrying ancient traditions forward.',
  count: 44,
  type: 'indigenous',
  region: 'East Asia'
}, {
  id: 'chinese',
  name: 'Chinese',
  description: "Stories from mainland China, Hong Kong, and the global Chinese diaspora — one of history's great civilisations.",
  count: 167,
  type: null,
  region: 'East Asia'
}, {
  id: 'japanese',
  name: 'Japanese',
  description: 'Contemporary and historical Japanese culture — from samurai to manga, tradition to innovation.',
  count: 154,
  type: null,
  region: 'East Asia'
}, {
  id: 'korean',
  name: 'Korean',
  description: 'North and South Korean cultures, the Korean Wave, and a diaspora spanning the globe.',
  count: 138,
  type: null,
  region: 'East Asia'
}, {
  id: 'taiwanese',
  name: 'Taiwanese',
  description: 'Distinct Taiwanese identity — Indigenous roots, Han heritage, and a vibrant democratic culture.',
  count: 72,
  type: null,
  region: 'East Asia'
}, {
  id: 'chinese-american',
  name: 'Chinese American',
  description: 'Chinese American diasporic identity — from the railroad era to contemporary hyphenated experience.',
  count: 89,
  type: 'diaspora',
  region: 'East Asia'
},
// ── South Asia ──
{
  id: 'adivasi',
  name: 'Adivasi',
  description: "Indigenous peoples of South Asia — Santali, Gondi, Munda, and hundreds of other nations predating the subcontinent's empires.",
  count: 34,
  type: 'indigenous',
  region: 'South Asia'
}, {
  id: 'indian',
  name: 'Indian',
  description: "The vast diversity of cultures, languages, and traditions across the world's largest democracy.",
  count: 189,
  type: null,
  region: 'South Asia'
}, {
  id: 'pakistani',
  name: 'Pakistani',
  description: 'Pakistani culture, history, and contemporary identity — Punjabi, Sindhi, Pashtun, Baloch, and more.',
  count: 112,
  type: null,
  region: 'South Asia'
}, {
  id: 'bangladeshi',
  name: 'Bangladeshi',
  description: 'Bangladeshi culture, the Bengali literary tradition, and diaspora stories from London to New York.',
  count: 78,
  type: null,
  region: 'South Asia'
}, {
  id: 'sri-lankan',
  name: 'Sri Lankan',
  description: 'Sinhalese, Tamil, and Muslim Sri Lankan identities — an island of extraordinary cultural richness.',
  count: 61,
  type: null,
  region: 'South Asia'
}, {
  id: 'nepali',
  name: 'Nepali',
  description: 'Himalayan cultures and contemporary Nepali identity — Sherpa, Newari, Gurung, and more.',
  count: 43,
  type: null,
  region: 'South Asia'
}, {
  id: 'sikh',
  name: 'Sikh',
  description: 'Punjabi Sikh identity, faith, and diaspora experiences — from the Punjab to the world.',
  count: 67,
  type: null,
  region: 'South Asia'
},
// ── Southeast Asia ──
{
  id: 'hmong',
  name: 'Hmong',
  description: 'Indigenous Hmong peoples of Southeast Asia and their diaspora — a culture of resilience and rich textile art.',
  count: 34,
  type: 'indigenous',
  region: 'Southeast Asia'
}, {
  id: 'orang-asli',
  name: 'Orang Asli',
  description: 'The Indigenous peoples of Peninsular Malaysia — Semang, Senoi, and Aboriginal Malay groups.',
  count: 22,
  type: 'indigenous',
  region: 'Southeast Asia'
}, {
  id: 'thai',
  name: 'Thai',
  description: 'Thai culture, Theravada Buddhism, and contemporary Thai identity — from royal tradition to street food.',
  count: 89,
  type: null,
  region: 'Southeast Asia'
}, {
  id: 'vietnamese',
  name: 'Vietnamese',
  description: 'Vietnamese culture, history, and diaspora narratives — a people shaped by resilience and reinvention.',
  count: 98,
  type: null,
  region: 'Southeast Asia'
}, {
  id: 'indonesian',
  name: 'Indonesian',
  description: "Stories from the world's largest archipelago — Javanese, Balinese, Sundanese, and 300+ other cultures.",
  count: 76,
  type: null,
  region: 'Southeast Asia'
}, {
  id: 'cambodian',
  name: 'Cambodian',
  description: 'Khmer culture, the legacy of Angkor, and post-conflict Cambodian identity and diaspora.',
  count: 52,
  type: null,
  region: 'Southeast Asia'
}, {
  id: 'malaysian',
  name: 'Malaysian',
  description: 'Malay, Chinese Malaysian, and Tamil Malaysian multicultural stories from a uniquely plural society.',
  count: 64,
  type: null,
  region: 'Southeast Asia'
},
// ── Middle East & North Africa ──
{
  id: 'kurdish',
  name: 'Kurdish',
  description: "The stateless Kurdish people — one of the world's largest ethnic groups without a nation-state.",
  count: 56,
  type: 'indigenous',
  region: 'Middle East & North Africa'
}, {
  id: 'amazigh-berber',
  name: 'Amazigh / Berber',
  description: 'Indigenous North African peoples predating Arab conquest — Tuareg, Kabyle, Riffian, and more.',
  count: 43,
  type: 'indigenous',
  region: 'Middle East & North Africa'
}, {
  id: 'arab',
  name: 'Arab',
  description: 'Pan-Arab identity and the diverse cultures of the Arab world — from Morocco to the Gulf.',
  count: 134,
  type: null,
  region: 'Middle East & North Africa'
}, {
  id: 'persian-iranian',
  name: 'Persian / Iranian',
  description: "Ancient Persian heritage and contemporary Iranian identity — one of the world's great literary civilisations.",
  count: 98,
  type: null,
  region: 'Middle East & North Africa'
}, {
  id: 'turkish',
  name: 'Turkish',
  description: 'Ottoman heritage and modern Turkish identity — bridging Europe and Asia, East and West.',
  count: 87,
  type: null,
  region: 'Middle East & North Africa'
}, {
  id: 'lebanese',
  name: 'Lebanese',
  description: 'Lebanese culture, diaspora, and the complexity of modern Lebanon — a crossroads of civilisations.',
  count: 72,
  type: null,
  region: 'Middle East & North Africa'
}, {
  id: 'egyptian',
  name: 'Egyptian',
  description: "Contemporary Egyptian identity rooted in one of history's greatest civilisations.",
  count: 81,
  type: null,
  region: 'Middle East & North Africa'
},
// ── Central & South America ──
{
  id: 'quechua',
  name: 'Quechua',
  description: 'Indigenous Andean peoples of Peru, Bolivia, Ecuador, and beyond — heirs of the Inca civilisation.',
  count: 54,
  type: 'indigenous',
  region: 'Central & South America'
}, {
  id: 'mapuche',
  name: 'Mapuche',
  description: 'Indigenous peoples of southern Chile and Argentina — never fully conquered by the Inca or the Spanish.',
  count: 41,
  type: 'indigenous',
  region: 'Central & South America'
}, {
  id: 'guarani',
  name: 'Guaraní',
  description: 'Indigenous peoples of Paraguay, Brazil, and Argentina — whose language became a national tongue.',
  count: 36,
  type: 'indigenous',
  region: 'Central & South America'
}, {
  id: 'guatemalan-maya',
  name: 'Guatemalan Maya',
  description: "Maya Indigenous peoples of Guatemala — K'iche', Mam, Kaqchikel, and more — carrying ancient knowledge forward.",
  count: 38,
  type: 'indigenous',
  region: 'Central & South America'
}, {
  id: 'brazilian',
  name: 'Brazilian',
  description: "Contemporary Brazilian identity — one of the world's most diverse nations, shaped by Indigenous, African, and European roots.",
  count: 103,
  type: null,
  region: 'Central & South America'
}, {
  id: 'afro-brazilian',
  name: 'Afro-Brazilian',
  description: 'African-descended Brazilian cultures — Candomblé, Capoeira, and the heartbeat of Brazilian music and art.',
  count: 89,
  type: null,
  region: 'Central & South America'
}, {
  id: 'colombian',
  name: 'Colombian',
  description: 'The diverse cultures and identities of Colombia — Caribbean coast, Andean highlands, and Amazonian depth.',
  count: 76,
  type: null,
  region: 'Central & South America'
}, {
  id: 'haitian',
  name: 'Haitian',
  description: "Haitian Creole culture, Vodou traditions, and the legacy of the world's first successful slave revolution.",
  count: 67,
  type: null,
  region: 'Central & South America'
},
// ── Global / Diaspora ──
{
  id: 'african-diaspora',
  name: 'African Diaspora',
  description: 'Pan-African diasporic identity not tied to a single national origin — the global Black experience.',
  count: 167,
  type: 'diaspora',
  region: 'Global / Diaspora'
}, {
  id: 'south-asian-diaspora',
  name: 'South Asian Diaspora',
  description: 'Indian, Pakistani, Bangladeshi, and Sri Lankan diaspora experiences across the globe.',
  count: 143,
  type: 'diaspora',
  region: 'Global / Diaspora'
}, {
  id: 'chinese-diaspora',
  name: 'Chinese Diaspora',
  description: 'Overseas Chinese communities across Southeast Asia, the Americas, and beyond — the global Huaqiao.',
  count: 128,
  type: 'diaspora',
  region: 'Global / Diaspora'
}, {
  id: 'afro-latinx',
  name: 'Afro-Latinx',
  description: 'African-descended Latinx identities that cross geographic and cultural lines — Black and Latino at once.',
  count: 94,
  type: 'diaspora',
  region: 'Global / Diaspora'
}, {
  id: 'arab-diaspora',
  name: 'Arab Diaspora',
  description: 'Arab communities living outside the Arab world — navigating dual identities in the West and beyond.',
  count: 87,
  type: 'diaspora',
  region: 'Global / Diaspora'
}, {
  id: 'jewish-diaspora',
  name: 'Jewish Diaspora',
  description: 'Jewish diasporic identity across cultures, continents, and centuries — the original diaspora.',
  count: 112,
  type: 'diaspora',
  region: 'Global / Diaspora'
}];
// Region config: order, emoji, color scheme
const regionConfig: {
  name: string;
  emoji: string;
  cardColor: {
    light: string;
    dark: string;
  };
  defaultOpen: boolean;
}[] = [{
  name: 'Oceania',
  emoji: '🌊',
  cardColor: {
    light: 'bg-teal-50 text-teal-900 hover:bg-teal-100',
    dark: 'bg-teal-900/20 text-teal-200 hover:bg-teal-900/30'
  },
  defaultOpen: true
}, {
  name: 'Africa',
  emoji: '🌍',
  cardColor: {
    light: 'bg-amber-50 text-amber-900 hover:bg-amber-100',
    dark: 'bg-amber-900/20 text-amber-200 hover:bg-amber-900/30'
  },
  defaultOpen: false
}, {
  name: 'Europe',
  emoji: '🏛️',
  cardColor: {
    light: 'bg-slate-50 text-slate-900 hover:bg-slate-100',
    dark: 'bg-slate-700/30 text-slate-200 hover:bg-slate-700/40'
  },
  defaultOpen: false
}, {
  name: 'North America',
  emoji: '🌎',
  cardColor: {
    light: 'bg-orange-50 text-orange-900 hover:bg-orange-100',
    dark: 'bg-orange-900/20 text-orange-200 hover:bg-orange-900/30'
  },
  defaultOpen: false
}, {
  name: 'East Asia',
  emoji: '🏯',
  cardColor: {
    light: 'bg-rose-50 text-rose-900 hover:bg-rose-100',
    dark: 'bg-rose-900/20 text-rose-200 hover:bg-rose-900/30'
  },
  defaultOpen: false
}, {
  name: 'South Asia',
  emoji: '🕌',
  cardColor: {
    light: 'bg-violet-50 text-violet-900 hover:bg-violet-100',
    dark: 'bg-violet-900/20 text-violet-200 hover:bg-violet-900/30'
  },
  defaultOpen: false
}, {
  name: 'Southeast Asia',
  emoji: '🌺',
  cardColor: {
    light: 'bg-emerald-50 text-emerald-900 hover:bg-emerald-100',
    dark: 'bg-emerald-900/20 text-emerald-200 hover:bg-emerald-900/30'
  },
  defaultOpen: false
}, {
  name: 'Middle East & North Africa',
  emoji: '🌙',
  cardColor: {
    light: 'bg-yellow-50 text-yellow-900 hover:bg-yellow-100',
    dark: 'bg-yellow-900/20 text-yellow-200 hover:bg-yellow-900/30'
  },
  defaultOpen: false
}, {
  name: 'Central & South America',
  emoji: '🌿',
  cardColor: {
    light: 'bg-pink-50 text-pink-900 hover:bg-pink-100',
    dark: 'bg-pink-900/20 text-pink-200 hover:bg-pink-900/30'
  },
  defaultOpen: false
}, {
  name: 'Global / Diaspora',
  emoji: '🌐',
  cardColor: {
    light: 'bg-indigo-50 text-indigo-900 hover:bg-indigo-100',
    dark: 'bg-indigo-900/20 text-indigo-200 hover:bg-indigo-900/30'
  },
  defaultOpen: false
}];
const CulturalIdentity: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const dk = useDarkClasses();
  const {
    darkMode
  } = useDarkMode();
  const contentNavItems: NavItem[] = [{
    label: 'View All Tags',
    href: '/tagged-books?type=all',
    icon: ListIcon
  }, {
    label: 'Tropes',
    href: '/tropes',
    icon: HeartHandshakeIcon,
    subItems: [{
      label: 'Romance',
      href: '/tropes?category=romance'
    }, {
      label: 'Fantasy',
      href: '/tropes?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/tropes?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/tropes?category=scifi'
    }, {
      label: 'Horror',
      href: '/tropes?category=horror'
    }, {
      label: 'General',
      href: '/tropes?category=general'
    }]
  }, {
    label: 'Characters',
    href: '/characters',
    icon: UserIcon,
    subItems: [{
      label: 'Romance',
      href: '/characters?category=romance'
    }, {
      label: 'Fantasy',
      href: '/characters?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/characters?category=mystery'
    }, {
      label: 'Sci-Fi',
      href: '/characters?category=scifi'
    }, {
      label: 'Horror',
      href: '/characters?category=horror'
    }, {
      label: 'General',
      href: '/characters?category=general'
    }]
  }, {
    label: 'Relationship Types',
    href: '/relationship-types',
    icon: UsersIcon,
    subItems: [{
      label: 'Romance',
      href: '/relationship-types?category=romance'
    }, {
      label: 'Fantasy',
      href: '/relationship-types?category=fantasy'
    }, {
      label: 'Mystery',
      href: '/relationship-types?category=mystery'
    }, {
      label: 'General',
      href: '/relationship-types?category=general'
    }]
  }, {
    label: 'Pairing Types',
    href: '/pairing-types',
    icon: LinkIcon,
    subItems: [{
      label: 'Monogamous',
      href: '/pairing-types?category=monogamous'
    }, {
      label: 'Why Choose',
      href: '/pairing-types?category=why-choose'
    }, {
      label: 'Closed Triad',
      href: '/pairing-types?category=closed-triad'
    }, {
      label: 'Polyamorous',
      href: '/pairing-types?category=polyamorous'
    }, {
      label: 'NB / Trans Inclusive',
      href: '/pairing-types?category=nb-trans'
    }, {
      label: 'No Romance',
      href: '/pairing-types?category=no-romance'
    }]
  }, {
    label: 'Spice & Darkness',
    href: '/spice-darkness',
    icon: FlameIcon,
    subItems: [{
      label: 'Spice Levels',
      href: '/spice-darkness#spice'
    }, {
      label: 'Darkness Levels',
      href: '/spice-darkness#darkness'
    }]
  }, {
    label: 'Burn Levels',
    href: '/burn-levels',
    icon: ZapIcon
  }, {
    label: 'Ending Types',
    href: '/ending-types',
    icon: BookmarkIcon
  }, {
    label: 'Representation',
    href: '/representation',
    icon: HeartIcon
  }, {
    label: 'Cultural Identity',
    href: '/cultural-identity',
    icon: GlobeIcon,
    isActive: true
  }, {
    label: 'Themes',
    href: '/themes',
    icon: BookOpenIcon
  }, {
    label: 'Settings',
    href: '/settings',
    icon: MapPinIcon,
    subItems: [{
      label: 'Contemporary',
      href: '/settings?category=Contemporary'
    }, {
      label: 'Historical',
      href: '/settings?category=Historical'
    }, {
      label: 'Fantasy',
      href: '/settings?category=Fantasy'
    }, {
      label: 'Science Fiction',
      href: '/settings?category=Science%20Fiction'
    }]
  }, {
    label: 'Content Warnings',
    href: '/content-warnings',
    icon: AlertCircleIcon
  }];
  // Filter identities by search
  const filteredIdentities = searchQuery ? culturalIdentities.filter(id => id.name.toLowerCase().includes(searchQuery.toLowerCase()) || id.description.toLowerCase().includes(searchQuery.toLowerCase()) || id.region.toLowerCase().includes(searchQuery.toLowerCase())) : culturalIdentities;
  // Group by region
  const groupedByRegion = regionConfig.reduce((acc, region) => {
    const items = filteredIdentities.filter(id => id.region === region.name);
    if (items.length > 0) acc[region.name] = items;
    return acc;
  }, {} as Record<string, CulturalIdentity[]>);
  const getTypeBadge = (type: IdentityType) => {
    if (type === 'indigenous') {
      return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-9323" data-mp="1bjge5qul1dln">
          Indigenous
        </span>;
    }
    if (type === 'diaspora') {
      return <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`} data-id="element-9324" data-mp="nsj98y17fgjw">
          Diaspora
        </span>;
    }
    return null;
  };
  return <div className={dk.page} data-id="element-9325" data-mp="139j07z89zhmg">
      <ContentPageHeader title="Cultural Identity" icon={<GlobeIcon className="h-6 w-6 text-indigo-500" data-id="element-9327" data-mp="z87altpeiwe9" />} searchPlaceholder="Search cultural identities..." searchQuery={searchQuery} setSearchQuery={setSearchQuery} showFilters={sidebarOpen} setShowFilters={setSidebarOpen} data-id="element-9326" data-mp="13bc9lma9wfy5" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-id="element-9328" data-mp="pc47akitcrpq">
        <div className="mb-4" data-id="element-9329" data-mp="khzz5xxtadn2">
          <BackButton to="/representation" label="Back to Representation" data-id="element-9330" data-mp="khq4vy5qhkbk" />
        </div>
        <div className="flex gap-8" data-id="element-9331" data-mp="fidmbplwazes">
          <FilterSidebarWrapper isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} isCollapsed={sidebarCollapsed} onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)} data-id="element-9332" data-mp="bjei677zn6oe">
            <FilterSidebar title="Browse" navigation={contentNavItems} sections={[]} onReset={() => setSearchQuery('')} searchable={false} data-id="element-9333" data-mp="1lgvvp9ovjyf" />
          </FilterSidebarWrapper>

          <main className="flex-1 min-w-0" data-id="element-9334" data-mp="2n5op03mmwlr7">
            {/* Legend */}
            <div className={`flex flex-wrap items-center gap-3 mb-6 px-4 py-3 rounded-lg text-sm ${darkMode ? 'bg-gray-800/50 border border-gray-700' : 'bg-gray-50 border border-gray-200'}`} data-id="element-9335" data-mp="2r7ti91967gdb">
              <span className={`font-medium ${darkMode ? 'text-gray-300' : 'text-gray-600'}`} data-id="element-9336" data-mp="1zg46flue7qig">
                Labels:
              </span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${darkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800'}`} data-id="element-9337" data-mp="23geagsymk6uo">
                Indigenous
              </span>
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9338" data-mp="1frlckqyfzoxf">
                — Identifies an Indigenous people or nation
              </span>
              <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ml-2 ${darkMode ? 'bg-blue-900/40 text-blue-300' : 'bg-blue-100 text-blue-800'}`} data-id="element-9339" data-mp="ugsr5s2h6dot">
                Diaspora
              </span>
              <span className={`text-xs ${darkMode ? 'text-gray-500' : 'text-gray-400'}`} data-id="element-9340" data-mp="lj8jo1e0rl5r">
                — Identity not tied exclusively to geographic origin
              </span>
            </div>

            {/* Search results: flat grid */}
            {searchQuery ? filteredIdentities.length === 0 ? <div className="text-center py-12" data-id="element-9341" data-mp="2rphtxvqed5b">
                  <p className={dk.textMuted} data-id="element-9342" data-mp="3jyro4yy1xv98">
                    No cultural identities found matching "{searchQuery}".
                  </p>
                </div> : <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-9343" data-mp="3ozz07a789glp">
                  {filteredIdentities.map(identity => {
              const region = regionConfig.find(r => r.name === identity.region);
              const cardColor = region ? darkMode ? region.cardColor.dark : region.cardColor.light : darkMode ? 'bg-gray-800 text-gray-200' : 'bg-gray-50 text-gray-900';
              return <Link key={identity.id} to={`/tagged-books/${identity.id}`} className={`${cardColor} rounded-lg p-3 transition-colors`} data-id="element-9344" data-mp="1v2oqjk1ydq6">
                        <div className="flex items-start justify-between gap-2 mb-1" data-id="element-9345" data-mp="bubgr6vcxi9w">
                          <span className="font-semibold text-sm leading-tight" data-id="element-9346" data-mp="6vhzd3cy974g">
                            {identity.name}
                          </span>
                          {getTypeBadge(identity.type)}
                        </div>
                        <p className="text-xs opacity-80 line-clamp-2 mb-1.5" data-id="element-9347" data-mp="iunb7u4anvk3">
                          {identity.description}
                        </p>
                        <span className="text-xs opacity-60" data-id="element-9348" data-mp="snkzkgvyweqm">
                          {identity.count} books
                        </span>
                      </Link>;
            })}
                </div> /* Region accordion sections */ : <div className="space-y-3" data-id="element-9349" data-mp="16ncau9v3nh0x">
                {regionConfig.map(region => {
              const items = groupedByRegion[region.name];
              if (!items) return null;
              const cardColor = darkMode ? region.cardColor.dark : region.cardColor.light;
              return <ContentAccordionSection key={region.name} title={`${region.emoji} ${region.name}`} itemCount={items.length} defaultOpen={region.defaultOpen} data-id="element-9350" data-mp="35t989mitzy2e">
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3" data-id="element-9351" data-mp="2w3pnucfgzw5f">
                        {items.map(identity => <Link key={identity.id} to={`/tagged-books/${identity.id}`} className={`${cardColor} rounded-lg p-3 transition-colors`} data-id="element-9352" data-mp="3osn7ojzby10a">
                            <div className="flex items-start justify-between gap-2 mb-1" data-id="element-9353" data-mp="3ew0nzyiqpfzu">
                              <span className="font-semibold text-sm leading-tight" data-id="element-9354" data-mp="39ul2y6gkb9hg">
                                {identity.name}
                              </span>
                              {getTypeBadge(identity.type)}
                            </div>
                            <p className="text-xs opacity-80 line-clamp-2 mb-1.5" data-id="element-9355" data-mp="3n9lmhzsiko1m">
                              {identity.description}
                            </p>
                            <span className="text-xs opacity-60" data-id="element-9356" data-mp="3dbp7gwwgh8y8">
                              {identity.count} books
                            </span>
                          </Link>)}
                      </div>
                    </ContentAccordionSection>;
            })}
              </div>}
          </main>
        </div>
      </div>
    </div>;
};
export default CulturalIdentity;